/* Local checkout-continuation candidate. No automatic install unless all three
 * explicit gates are true. Frontend flow control is not fee enforcement. */
(() => {
  'use strict';
  const FORM_IDS = new Set(['cart', 'CartDrawer-Form', 'cart-notification-form']);
  const UPDATES = /^updates(?:\[|$)/;
  const assert = (condition, message) => { if (!condition) throw new Error(message); };
  const hasPersonalization = cart => cart.items.some(item => Object.keys(item.properties || {}).some(key => key.startsWith('_pc_')));
  function inspectFields(entries) {
    const metadata = {}, unsupported = [];
    for (const [name, value] of entries) {
      if (UPDATES.test(name) || name === 'checkout') continue;
      if (name === 'note' || /^attributes\[[^\]]+\]$/.test(name)) {
        assert(typeof value === 'string', 'A cart option could not be preserved.');
        assert(!Object.hasOwn(metadata, name) || metadata[name] === value, 'Conflicting cart options need review before checkout.');
        metadata[name] = value;
      } else unsupported.push(name);
    }
    return {metadata, unsupported};
  }
  function createController({client, runtime}) {
    let busy = false;
    return Object.freeze({
      get busy() { return busy; },
      async prepare({validatePersonalizedCart}) {
        assert(client?.checkoutContinuationEnabled && client.enabled && runtime, 'Personalization checkout continuation is not enabled.');
        assert(!busy, 'Checkout is already being prepared.'); busy = true;
        try {
          const revision = await runtime.whenSettled();
          const before = await client.readCurrentCart(); runtime.assertIdle(revision);
          assert(Array.isArray(before?.items), 'Your cart could not be checked.');
          if (!hasPersonalization(before)) return {personalized: false, cart: before, revision};
          assert(!client.needsReview, 'Review your cart before continuing to checkout.');
          await validatePersonalizedCart(before); runtime.assertIdle(revision);
          const plan = await client.prepareReconciliation(); runtime.assertIdle(revision);
          const after = await client.applyReconciliation(plan); runtime.assertIdle(revision);
          const latest = await client.readCurrentCart(); runtime.assertIdle(revision);
          assert(await client.hashCart(latest) === await client.hashCart(after), 'Your cart changed after personalization was checked. Review it before checkout.');
          return {personalized: true, cart: latest, revision};
        } finally { busy = false; }
      },
    });
  }
  function resumeWithoutUpdates({form, submitter, permit, onCancelled, onAccepted = () => undefined}) {
    // Keep the original form and native submit event/validation. Excluding only
    // the stale line-update controls preserves note/attributes/required fields.
    const updates = [...form.elements].filter(control => UPDATES.test(control.name || ''));
    const previous = updates.map(control => control.disabled);
    updates.forEach(control => { control.disabled = true; });
    let nativeEvent = null;
    const observe = event => { nativeEvent = event; };
    form.addEventListener('submit', observe, {once: true});
    const restore = () => updates.forEach((control, index) => { control.disabled = previous[index]; });
    try {
      permit(form);
      form.requestSubmit(submitter);
    } catch (error) { restore(); form.removeEventListener('submit', observe); throw error; }
    // If native/app validation cancels, restore controls and refresh stale cart
    // markup. Otherwise keep them excluded until navigation, never re-enable
    // stale updates during the native form's entry-list construction.
    queueMicrotask(() => {
      if (!nativeEvent || nativeEvent.defaultPrevented) { restore(); form.removeEventListener('submit', observe); onCancelled(); }
      else onAccepted();
    });
    return restore;
  }
  function install({document, client, runtime}) {
    if (!client?.checkoutContinuationEnabled || !client.enabled || !runtime) return null;
    const controller = createController({client, runtime}), permitted = new WeakSet();
    let handling = false, navigating = false;
    const currentForm = id => document.getElementById(id);
    const errorFor = (form, message) => {
      let node = form.querySelector('[data-wcf-checkout-error]');
      if (!node) { node = document.createElement('p'); node.dataset.wcfCheckoutError = ''; node.setAttribute('role', 'alert'); node.tabIndex = -1; form.append(node); }
      node.textContent = message; node.focus({preventScroll: true});
    };
    const snapshot = form => {
      const parsed = inspectFields(new FormData(form).entries());
      const drawerNote = form.id === 'CartDrawer-Form' ? document.getElementById('CartDrawer-Note') : null;
      if (drawerNote && !drawerNote.disabled) parsed.metadata.note = drawerNote.value;
      return parsed;
    };
    const validateQuantities = (form, cart) => {
      for (const control of [...form.elements].filter(control => UPDATES.test(control.name || '') && control.type !== 'hidden' && !control.disabled)) {
        const key = control.dataset.quantityLineKey;
        assert(key && cart.items.some(item => item.key === key && item.quantity === Number(control.value)), 'A quantity is still changing. Wait for the cart update, then try checkout again.');
      }
    };
    const handler = async event => {
      const form = event.target, button = event.submitter;
      if (!FORM_IDS.has(form?.id) || form.dataset.wcfPcCheckout !== 'true' || !button || button.name !== 'checkout' || button.form !== form) return;
      if (permitted.has(form)) { permitted.delete(form); return; }
      if (event.defaultPrevented) return; // Respect earlier native/app validation.
      event.preventDefault();
      if (handling || navigating) return; handling = true;
      const id = form.id, buttonId = button.id;
      const surfaces = [...document.querySelectorAll('[data-wcf-pc-checkout-surface]')];
      const beforeInert = surfaces.map(node => node.inert);
      const beforeDisabled = button.disabled; button.disabled = true;
      surfaces.forEach(node => { node.inert = true; });
      let metadata;
      try {
        const prepared = await controller.prepare({validatePersonalizedCart: cart => {
          const fresh = currentForm(id); assert(fresh?.dataset.wcfPcCheckout === 'true', 'The cart changed. Review it before checkout.');
          const fields = snapshot(fresh);
          assert(!fields.unsupported.length, 'An additional cart option needs review before personalized checkout.');
          assert(fresh.checkValidity(), 'Complete the required cart fields before checkout.');
          validateQuantities(fresh, cart); metadata = JSON.stringify(fields.metadata);
        }});
        const fresh = currentForm(id); assert(fresh?.dataset.wcfPcCheckout === 'true' && fresh.isConnected, 'The cart changed. Review it before checkout.');
        const freshButton = buttonId ? document.getElementById(buttonId) : fresh.querySelector('[name="checkout"]');
        assert(freshButton?.form === fresh && freshButton.name === 'checkout', 'The checkout button changed. Review your cart.');
        runtime.assertIdle(prepared.revision);
        if (prepared.personalized) assert(JSON.stringify(snapshot(fresh).metadata) === metadata, 'Your cart options changed. Review them before checkout.');
        surfaces.forEach((node, index) => { node.inert = beforeInert[index]; });
        button.disabled = beforeDisabled; freshButton.disabled = false;
        if (prepared.personalized) {
          resumeWithoutUpdates({form: fresh, submitter: freshButton, permit: value => permitted.add(value),
            onAccepted: () => { navigating = true; },
            onCancelled: () => { permitted.delete(fresh); errorFor(fresh, 'Checkout was stopped by a required cart check. Reload the cart to review its latest contents.'); }});
        } else {
          let resumed = null;
          const observe = value => { resumed = value; };
          fresh.addEventListener('submit', observe, {once: true}); permitted.add(fresh); fresh.requestSubmit(freshButton);
          queueMicrotask(() => { permitted.delete(fresh); fresh.removeEventListener('submit', observe); navigating = Boolean(resumed && !resumed.defaultPrevented); });
        }
      } catch (error) {
        const fresh = currentForm(id) || form;
        permitted.delete(fresh);
        errorFor(fresh, error.message || 'Checkout could not be prepared. Review your cart and try again.');
      } finally {
        surfaces.forEach((node, index) => { node.inert = beforeInert[index]; });
        button.disabled = beforeDisabled; handling = false;
      }
    };
    document.addEventListener('submit', handler);
    // BFCache can restore a form whose line controls intentionally stayed
    // disabled for native checkout. Reload fresh cart HTML before editing.
    const onPageShow = event => { if (event.persisted) document.defaultView.location.reload(); };
    document.defaultView.addEventListener('pageshow', onPageShow);
    return {destroy() { document.removeEventListener('submit', handler); document.defaultView.removeEventListener('pageshow', onPageShow); }};
  }
  const api = {inspectFields, createController, resumeWithoutUpdates, install};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.wcfPersonalizationCheckoutContinuation = api;
})();
